import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'; 
import Modal from 'react-modal';
import { Line, Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import '../styles/Filter.css';
import '../styles/ChartStyles.css';
import '../styles/FilterContent.css';
import { fetchTotalPostagens, fetchPostagens } from '../services/ApiService'; 

Chart.register(CategoryScale, LinearScale, LineElement, BarElement, Title, Tooltip, Legend, zoomPlugin);

Modal.setAppElement('#root'); 

function AnalysisPosts() {
    const recordsPerPage = 5;

    const [intervaloDatas, setIntervaloDatas] = useState({ inicio: '', fim: '' });
    const [selectedPeriodos, setSelectedPeriodos] = useState(['Matutino']);
    const [selectedDias, setSelectedDias] = useState(['Monday']);
    const [filtroTexto, setFiltroTexto] = useState('');
    const [selectedCampos, setSelectedCampos] = useState(['título']);
    const [analysisType, setAnalysisType] = useState('textual');
    const [chartType, setChartType] = useState('line');
    const [postagens, setPostagens] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [loading, setLoading] = useState(false);
    const [noData, setNoData] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const chartRef = useRef(null);

    const handlePeriodChange = (e) => {
        const value = e.target.value;
        setSelectedPeriodos((prev) =>
            prev.includes(value)
                ? prev.filter((item) => item !== value)
                : [...prev, value]
        );
        setCurrentPage(1);
    };

    const handleDiasChange = (e) => {
        const value = e.target.value;
        setSelectedDias((prev) =>
            prev.includes(value)
                ? prev.filter((item) => item !== value)
                : [...prev, value]
        );
        setCurrentPage(1);
    };

    const handleCampoChange = (e) => {
        const value = e.target.value;
        setSelectedCampos((prev) =>
            prev.includes(value)
                ? prev.filter((item) => item !== value)
                : [...prev, value]
        );
        setCurrentPage(1);
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setIntervaloDatas(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const handleFilterTextChange = (e) => {
        setFiltroTexto(e.target.value);
        setCurrentPage(1);
    };

    const handleAnalysisTypeChange = (e) => {
        setAnalysisType(e.target.value);
        setCurrentPage(1);
    };

    const [dataInicioTotal, setDataInicioTotal] = useState('');
    const [dataFimTotal, setDataFimTotal] = useState([]); 
    const fetchTotalPostagensData = useCallback(async () => {
        try {
            const { dataInicioTotal, dataFimTotal } = await fetchTotalPostagens();
            setDataInicioTotal(dataInicioTotal);
            setDataFimTotal(dataFimTotal);
        } catch (error) {
            console.error('Erro ao buscar postagens totais:', error);
        }
    }, []);

    useEffect(() => {
        fetchTotalPostagensData();
    }, [fetchTotalPostagensData]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setNoData(false);
        try {
            const response = await fetchPostagens({
                intervaloDatas: JSON.stringify(intervaloDatas),
                periodoDia: selectedPeriodos.join(','),
                diasSemana: selectedDias.join(','),
                filtroTexto,
                campoTexto: selectedCampos.join(',')
            });

            if (response && response.length > 0) {
                setPostagens(response);
                setTotalRegistros(response.length);

                if (analysisType === 'gráfica') {
                    const postagensPorMes = response.reduce((acc, post) => {
                        const mes = post._source.data_postagem.slice(0, 7);
                        acc[mes] = (acc[mes] || 0) + 1;
                        return acc;
                    }, {});

                    const sortedDates = Object.keys(postagensPorMes).sort((a, b) => new Date(a) - new Date(b));
                    const sortedCounts = sortedDates.map(date => postagensPorMes[date]);

                    setChartData({
                        labels: sortedDates,
                        datasets: [
                            {
                                label: 'Total de Postagens por Mês',
                                data: sortedCounts,
                                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                                borderColor: 'rgba(75, 192, 192, 1)',
                                borderWidth: 2,
                                fill: false,
                            },
                        ],
                    });
                }
            } else {
                setNoData(true);
                setTotalRegistros(0);
            }
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        }
        setLoading(false);
    }, [intervaloDatas, selectedPeriodos, selectedDias, filtroTexto, selectedCampos, analysisType]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const exportChart = () => {
        if (chartRef.current) {
            html2canvas(chartRef.current.canvas)
                .then((canvas) => {
                    const link = document.createElement('a');
                    link.href = canvas.toDataURL('image/png');
                    link.download = 'analysis_chart.png';
                    link.click();
                })
                .catch((error) => {
                    console.error('Erro ao exportar o gráfico:', error);
                });
        } else {
            console.error('Referência ao gráfico não está disponível');
        }
    };

    const exportData = () => {
        if (chartData) {
            const csvData = chartData.labels.map((label, index) => {
                return `${label},${chartData.datasets[0].label},${chartData.datasets[0].data[index]}`;
            }).join('\n');

            const blob = new Blob([`Data,Legenda,Total\n${csvData}`], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'analysis_data.csv';
            link.click();
        } else {
            console.error('Dados do gráfico não estão disponíveis');
        }
    };

    const chartOptions = {
        plugins: {
            title: {
                display: true,
                text: 'Análise das Postagens por Mês',
                font: {
                    size: 18,
                },
            },
            legend: {
                display: true,
                position: 'top',
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x',
                },
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Data',
                },
                ticks: {
                    maxTicksLimit: 10,
                    maxRotation: 90,
                    minRotation: 45,
                    autoSkip: true,
                },
                grid: {
                    display: true,
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Número de Postagens',
                },
                beginAtZero: true,
                grid: {
                    display: true,
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
    };

    const paginate = (items, pageNumber, pageSize) => {
        const start = (pageNumber - 1) * pageSize;
        return items.slice(start, start + pageSize);
    };

    const currentPosts = paginate(postagens, currentPage, recordsPerPage);

    const toggleModal = () => {
        setModalIsOpen(!modalIsOpen);
    };

    const handleTermClick = (term) => {
        setFiltroTexto(prev => prev ? `${prev},${term}` : term);
        toggleModal();
    };

    return (
        <div className="filter-container">
            <h1 className="page-title-analyses">Montar Consulta</h1>
            <p className="page-subtitle-analyses">Use os filtros abaixo para visualizar as postagens de acordo com os critérios selecionados.</p>
            <p className="data-period">Os dados apresentados foram coletados entre <strong>{dataInicioTotal}</strong> e <strong>{dataFimTotal}</strong>.</p>

            <div className="filtro-row">
                <div className="filtro-item">
                    <label>Formato de Saída:</label>
                    <select value={analysisType} onChange={handleAnalysisTypeChange}>
                        <option value="textual">Textual</option>
                        <option value="gráfica">Gráfico</option>
                    </select>
                </div>
                <div className="filtro-item">
                    <label>Data Inicial:</label>
                    <input 
                        type="date" 
                        name="inicio"
                        value={intervaloDatas.inicio} 
                        onChange={handleDateChange} 
                    />
                </div>

                <div className="filtro-item">
                    <label>Data Final:</label>
                    <input 
                        type="date" 
                        name="fim"
                        value={intervaloDatas.fim} 
                        onChange={handleDateChange} 
                    />
                </div>
            </div>

            <div className="filtro-row">
                <div className="filtro-item">
                    <label>Período do Dia:</label>
                    <div className="filtro-checkbox-group">
                        {["Madrugada", "Matutino", "Vespertino", "Noturno"].map((periodo) => (
                            <label key={periodo}>
                                <input
                                    type="checkbox"
                                    value={periodo}
                                    checked={selectedPeriodos.includes(periodo)}
                                    onChange={handlePeriodChange}
                                />
                                {periodo}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="filtro-item">
                    <label>Dias da Semana:</label>
                    <div className="filtro-checkbox-group">
                        {[
                            { label: "Segunda", value: "Monday" },
                            { label: "Terça", value: "Tuesday" },
                            { label: "Quarta", value: "Wednesday" },
                            { label: "Quinta", value: "Thursday" },
                            { label: "Sexta", value: "Friday" },
                            { label: "Sábado", value: "Saturday" },
                            { label: "Domingo", value: "Sunday" },
                        ].map((dia) => (
                            <label key={dia.value}>
                                <input
                                    type="checkbox"
                                    value={dia.value}
                                    checked={selectedDias.includes(dia.value)}
                                    onChange={handleDiasChange}
                                />
                                {dia.label}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="filtro-row">
                <div className="filtro-item">
                    <label>Filtrar por:</label>
                    <div className="filtro-checkbox-group">
                        {["Título", "Texto", "Comentários"].map((campo) => (
                            <label key={campo}>
                                <input
                                    type="checkbox"
                                    value={campo.toLowerCase()}
                                    checked={selectedCampos.includes(campo.toLowerCase())}
                                    onChange={handleCampoChange}
                                />
                                {campo}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="filtro-item">
                    <label>Conteúdo do Filtro:</label>
                    <div className="filter-input-container">
                        <input 
                            type="text" 
                            className="filtro-texto"  
                            value={filtroTexto} 
                            onChange={handleFilterTextChange} 
                            placeholder="Digite as palavras-chave separadas por vírgula" 
                        />
                        <FontAwesomeIcon 
                            icon={faInfoCircle}  
                            className="filter-icon" 
                            onClick={toggleModal} 
                            title="Termos Sugeridos para Busca" // Tooltip
                        />
                        <FontAwesomeIcon 
                            icon={faTimesCircle}  
                            className="clear-icon" 
                            onClick={() => setFiltroTexto('')} 
                            title="Limpar Filtro" // Tooltip
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={toggleModal}
                contentLabel="Sugestões de Termos"
                className="suggestion-modal"
                overlayClassName="suggestion-overlay"
            >
                <h2>Sugestões de Termos</h2>
                <ul className="term-list">
                    {[
                        'depre', 'ansiedade', 'chorar', 'morrer', 'matar', 'medo', 'crises',
                        'chorando', 'só', 'sozinho', 'solidão', 'desolado', 'desanimado',
                        'desmotivado', 'doloroso', 'dor', 'dores', 'frustrado', 'insônia',
                        'machucado', 'morreu', 'morte', 'depressão'
                    ].map((term) => (
                        <li key={term} onClick={() => handleTermClick(term)}>
                            {term}
                        </li>
                    ))}
                </ul>
                <button onClick={toggleModal}>Fechar</button>
            </Modal>

            {analysisType === 'gráfica' && (
                <div className="filtro-row">
                    <div className="filtro-item">
                        <label>Tipo de Gráfico:</label>
                        <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                            <option value="line">Linha</option>
                            <option value="bar">Barra</option>
                        </select>
                    </div>
                    <div className="filtro-item">
                        <label>Total de Registros:</label>
                        <div className="total-registros">
                            {totalRegistros} registros encontrados
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <p>Carregando dados...</p>
            ) : noData ? (
                <p>Nenhuma postagem encontrada para os filtros aplicados.</p>
            ) : analysisType === 'textual' ? (
                <>
                    <div className="filtro-row">
                        <div className="filtro-item">
                            <label>Total de Registros:</label>
                            <div className="total-registros">
                                {totalRegistros} registros encontrados
                            </div>
                        </div>
                    </div>
                    <div className="postagens-container">
                        {currentPosts.map((postagem, index) => (
                            <div key={index} className="postagem-item">
                               {selectedCampos.includes('título') && (
                                    <>
                                        <h3>Título da Postagem:</h3>
                                        {postagem._source.titulo && postagem._source.titulo.trim() !== '' ? (
                                            filtroTexto.split(',').every(term => postagem._source.titulo.toLowerCase().includes(term.trim().toLowerCase())) ? (
                                                <p>{postagem._source.titulo}</p>
                                            ) : (
                                                <p>Postagem não contém o termo "{filtroTexto}" no título.</p>
                                            )
                                        ) : (
                                            <p>Postagem sem título.</p>
                                        )}
                                    </>
                                )}
                               {selectedCampos.includes('texto') && (
                                    <>
                                        <h3>Texto da Postagem:</h3>
                                        {postagem._source.texto && postagem._source.texto.trim() !== '' ? (
                                            filtroTexto.split(',').every(term => postagem._source.texto.toLowerCase().includes(term.trim().toLowerCase())) ? (
                                                <p>{postagem._source.texto}</p>
                                            ) : (
                                                <p>Postagem não contém o termo "{filtroTexto}" no texto.</p>
                                            )
                                        ) : (
                                            <p>Postagem sem texto.</p>
                                        )}
                                    </>
                                )}
                                {selectedCampos.includes('comentários') && (
                                    <>
                                        <h3>Comentários:</h3>
                                        {postagem._source.comentarios && postagem._source.comentarios.length > 0 ? (
                                            postagem._source.comentarios
                                                .filter((comentario) =>
                                                    filtroTexto.split(',').every(term => comentario.toLowerCase().includes(term.trim().toLowerCase()))
                                                ).length > 0 ? (
                                                    postagem._source.comentarios
                                                        .filter((comentario) =>
                                                            filtroTexto.split(',').every(term => comentario.toLowerCase().includes(term.trim().toLowerCase()))
                                                        )
                                                        .map((comentario, i) => (
                                                            <div key={i} style={{ marginBottom: '10px' }}>
                                                                <strong>Comentário {i + 1}:</strong>
                                                                <p>{comentario}</p>
                                                            </div>
                                                        ))
                                                ) : (
                                                    <p>Postagem não contém comentários com o termo "{filtroTexto}".</p>
                                                )
                                        ) : (
                                            <p>Postagem sem comentários.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                        <div className="button-container">
                            <button 
                                className="pagination-button" 
                                onClick={() => setCurrentPage(currentPage - 1)} 
                                disabled={currentPage === 1}
                            >
                                Anterior
                            </button>
                            <button 
                                className="pagination-button" 
                                onClick={() => setCurrentPage(currentPage + 1)} 
                                disabled={currentPosts.length < recordsPerPage}
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                chartData && (
                    <div className="analysis-chart-container">
                        <div style={{ height: '500px', width: '100%' }}>
                            {chartType === 'line' ? (
                                <Line ref={chartRef} data={chartData} options={chartOptions} />
                            ) : (
                                <Bar ref={chartRef} data={chartData} options={chartOptions} />
                            )}
                        </div>
                        <div className="button-container">
                            <button className="export-button" onClick={exportData}>Exportar Dados</button>
                            <button className="export-button" onClick={exportChart}>Exportar Gráfico</button>
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

export default AnalysisPosts;
